import React, { useEffect, useState } from 'react';
import { Row, Col, Form, Button, Image } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { ContentContainer } from '../../components';
import { createUser } from '../../actions/createUserActions';
import normalization from 'cdfdigital-common/build/helper/normalization';
import validate from 'cdfdigital-common/build/helper/validation';
import { LoginState } from 'cdfdigital-common/src/models/Login';
import decodeJwt from '../../helper/decode-jwt';

const HolderForm: React.FC = () => {
  const dispatch = useDispatch();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      cpf: '',
      phone: '',
      email: '',
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  // const { slug } = useSelector<any, any>((state) => state.labelReducer);
  const { data: { token } } = useSelector<any, LoginState>((state) => state.loginReducer);

  const [view, setView] = useState<'form' | 'success' | 'error'>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;

    try {
      const payload = decodeJwt(token);
      //console.log('decoded payload:', payload);
      const newView = payload?.hasPendingNamuLicenses ? 'form' : 'success';
      setView(prev => (prev === newView ? prev : newView));
    } catch {
      setView(prev => (prev === 'form' ? prev : 'form'));
    }
  }, [token]);

  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      const normalizedData = {
        ...data,
        cpf: data.cpf.replace(/\D/g, ''),
        phone: data.phone.replace(/\D/g, ''),
      };

      const result = await dispatch(createUser(normalizedData)) as any;
      //console.log(result);
      if (result.payload?.success) {
        return setView('success');
      }
  
      if (result.error) {
        setView('error');
      }
    } catch (error) {
      setView('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContentContainer id="user-form-container">
      {view === 'form' && (
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col xs={12} className="form-title">
              <h2 className="page-title">Dados do titular:</h2>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col xs={12} md={6} className="mb-3">
              <div className="form-group">
                <Form.Label htmlFor="input-name">Nome</Form.Label>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    validate: validate.notEmpty,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      id="input-name"
                      placeholder="Digite seu nome"
                      className="field-required form-control"
                      isInvalid={!!errors.name}
                    />
                  )}
                />
                {errors.name && (
                  <div className="invalid-feedback">Nome é obrigatório</div>
                )}
              </div>
            </Col>
            <Col xs={12} md={6} className="mb-3">
              <div className="form-group">
                <Form.Label htmlFor="input-cpf">CPF</Form.Label>
                <Controller
                  name="cpf"
                  control={control}
                  rules={{
                    validate: validate.cpfLength,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      id="input-cpf"
                      placeholder="Digite seu CPF"
                      className="field-required form-control"
                      maxLength={14}
                      value={normalization.cpf(field.value)}
                      onChange={(e) => {
                        const normalizedValue = normalization.cpf(
                          e.target.value,
                        );
                        field.onChange(normalizedValue);
                      }}
                      isInvalid={!!errors.cpf}
                    />
                  )}
                />
                {errors.cpf && (
                  <div className="invalid-feedback">CPF inválido</div>
                )}
              </div>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col xs={12} md={6} className="mb-3">
              <div className="form-group">
                <Form.Label htmlFor="input-phone">Telefone</Form.Label>
                <Controller
                  name="phone"
                  control={control}
                  rules={{
                    validate: validate.telLength,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      id="input-phone"
                      placeholder="Digite seu telefone"
                      className="field-required form-control"
                      maxLength={15}
                      value={normalization.tel(field.value)}
                      onChange={(e) => {
                        const normalizedValue = normalization.tel(
                          e.target.value,
                        );
                        field.onChange(normalizedValue);
                      }}
                      isInvalid={!!errors.phone}
                    />
                  )}
                />
                {errors.phone && (
                  <div className="invalid-feedback">Telefone inválido</div>
                )}
              </div>
            </Col>
            <Col xs={12} md={6} className="mb-3">
              <div className="form-group">
                <Form.Label htmlFor="input-email">E-mail</Form.Label>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    validate: validate.email,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      id="input-email"
                      type="email"
                      placeholder="Digite seu email"
                      className="field-required form-control"
                      isInvalid={!!errors.email}
                    />
                  )}
                />
                {errors.email && (
                  <div className="invalid-feedback">E-mail inválido</div>
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} className="text-end">
              <Button bsPrefix="btn btn-primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Enviando...' : 'Enviar'}
              </Button>
            </Col>
          </Row>
        </Form>
      )}

      {view === 'success' && (
        <div id="success">
          <p>
            Seu acesso foi criado, baixe o aplicativo na loja para começar a
            usar.
          </p>
          <div className="container-download">
            <a
              href="https://play.google.com/store/apps/details?id=br.com.namu&hl=pt_BR"
              target="_blank"
              rel="noopener noreferrer"
            >
              {/* FIXME This SRC value is hardcoded because slug seemed to be causing issues */}
              <Image src={`/tokiomarine/google-play-badge.webp`} width={300} />
            </a>
            <a
              href="https://apps.apple.com/br/app/namu-wellness/id1544610300"
              target="_blank"
              rel="noopener noreferrer"
            >
              {/* FIXME This SRC value is hardcoded because slug seemed to be causing issues */}
              <Image src={`/tokiomarine/app-store-badge.webp`} width={300} />
            </a>
          </div>
        </div>
      )}

      {view === 'error' && (
        <div id="error">
          <p>Não foi possível ativar o seu serviço.</p>
          <p>Tente novamente mais tarde.</p>
          <div className="container-download">
            <a className="btn-service" href='tel:0800 707 5050'>
              Canal de atendimento Porto<br></br>0800 707 5050
            </a>
            <div className="btn-service" onClick={() => setView('form')}>Tentar novamente</div>
          </div>
        </div>
      )}
    </ContentContainer>
  );
};

export default HolderForm;
